import { Badge, Button, Input } from '@cloudflare/kumo';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { useAppToast } from '../../hooks/useAppToast';
import { DangerConfirmModal } from '../../components/DangerConfirmModal';
import { DataTable as Table } from '../../components/ui/DataTable';
import { ICONS } from '../../components/ui/icons';
import { PerfilHeader, quando } from './ui';

interface Identity { id: string; provider: string; label: string; email: string | null; createdAt: string; lastUsedAt: string | null }
interface Security { identities: Identity[]; providers: Record<string, boolean> }

/** Contas externas (Google, Microsoft…) autorizadas a entrar na sua conta */
export function AplicativosPage() {
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const [data, setData] = useState<Security | null>(null);
  const [busca, setBusca] = useState('');
  const [revoking, setRevoking] = useState<Identity | null>(null);

  const load = useCallback(async () => {
    const r = await fetchApi('/api/account/security');
    if (r.ok) setData(await r.json());
  }, [fetchApi]);
  useEffect(() => { load(); }, [load]);

  const linkados = new Set((data?.identities ?? []).map((i) => i.provider));
  const NOMES: Record<string, string> = { google: 'Google', microsoft: 'Microsoft', github: 'GitHub', facebook: 'Facebook' };
  const vincular = Object.entries(data?.providers ?? {}).filter(([id, on]) => on && !linkados.has(id)).map(([id]) => ({ id, label: NOMES[id] ?? id }));
  const rows = useMemo(() => (data?.identities ?? []).filter((i) => `${i.label} ${i.email ?? ''}`.toLowerCase().includes(busca.toLowerCase())), [data, busca]);

  const link = async (provider: string) => {
    const r = await fetchApi(`/api/account/identities/${provider}/link`, { method: 'POST' });
    const b = await r.json().catch(() => ({}));
    if (r.ok && b.url) window.location.href = b.url;
    else toast.error(b.error || 'Não foi possível vincular');
  };

  return (
    <>
      <PerfilHeader title="Aplicativos conectados" description="Contas externas que você autorizou a entrar no Ingrave Argentic." />
      <div className="px-6 py-6 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div className="w-96 max-w-full"><Input aria-label="Buscar por nome" placeholder="Buscar por nome" value={busca} onChange={(e) => setBusca(e.target.value)} /></div>
          <div className="flex gap-2">{vincular.map((p) => <Button key={p.id} variant="secondary" icon={ICONS.Plus} onClick={() => link(p.id)}>Vincular {p.label}</Button>)}</div>
        </div>
        <Table
          items={rows}
          loading={!data}
          trackBy="id"
          empty="Nenhum aplicativo conectado."
          columnDefinitions={[
            { id: 'app', header: 'Aplicativo', cell: (i: Identity) => <div><div className="font-medium text-kumo-default">{i.label}</div><div className="text-xs text-kumo-subtle">{i.email ?? '—'}</div></div> },
            { id: 'ultimo', header: 'Último acesso', cell: (i: Identity) => quando(i.lastUsedAt) },
            { id: 'desde', header: 'Conectado em', cell: (i: Identity) => <Badge variant="secondary">{quando(i.createdAt)}</Badge> },
            { id: 'acao', header: '', cell: (i: Identity) => <Button size="sm" variant="secondary" onClick={() => setRevoking(i)}>Revogar</Button> },
          ]}
        />
        <p className="text-sm text-kumo-subtle">{data ? `Mostrando ${rows.length} de ${data.identities.length} aplicativos conectados` : ''}</p>
      </div>
      <DangerConfirmModal open={!!revoking} title="Revogar aplicativo" confirmText="Revogar" objectName={revoking?.label} onCancel={() => setRevoking(null)}
        onConfirm={async () => {
          const i = revoking; setRevoking(null); if (!i) return;
          const r = await fetchApi(`/api/account/identities/${i.id}`, { method: 'DELETE' });
          const b = await r.json().catch(() => ({}));
          if (r.ok) { toast.success('Aplicativo revogado'); load(); } else toast.error(b.error || 'Não foi possível revogar');
        }}>
        {revoking ? `${revoking.label} deixa de poder entrar na sua conta.` : ''}
      </DangerConfirmModal>
    </>
  );
}

export default AplicativosPage;
